import { formatPrice } from "./Pool_Utils.js";

class Pool_SummaryManager {
    /**
     * Initializes the summary manager.
     */
    constructor() {
        this.summaryContainer = document.getElementById("summary");
    }

    /**
     * Updates the summary UI with selected components.
     * @param {Object|null} selectedPool - Selected pool data.
     * @param {Object|null} selectedLiner - Selected liner data.
     * @param {Object|null} selectedSkimmer - Selected skimmer data.
     * @param {Object|null} selectedEquipment - Selected equipment data.
     */
    updateSummary(selectedPool = null, selectedLiner = null, selectedSkimmer = null, selectedEquipment = null) {
        if (!this.summaryContainer) return;
        this.summaryContainer.innerHTML = this.getSummaryHTML(selectedPool, selectedLiner, selectedSkimmer, selectedEquipment);
    }

    /**
     * Generates summary HTML content for display.
     * Used for both the main summary and the review step.
     * @param {Object|null} selectedPool - Selected pool object.
     * @param {Object|null} selectedLiner - Selected liner object.
     * @param {Object|null} selectedSkimmer - Selected skimmer object.
     * @param {Object|null} selectedEquipment - Selected equipment object.
     * @param {boolean} isReviewStep - Whether this is for the final review step.
     * @returns {string} - Generated summary HTML.
     */
    getSummaryHTML(selectedPool, selectedLiner, selectedSkimmer, selectedEquipment, isReviewStep = false) {
        let subtotal = 0;
        const items = [
            selectedPool ? this.formatPoolSummary(selectedPool) : null,
            selectedLiner ? this.formatLinerSummary(selectedLiner) : null,
            selectedSkimmer,
            selectedEquipment?.filter,
            selectedEquipment?.pump
        ].filter(Boolean);

        const summaryHTML = items.map(item => {
            let itemPrice = parseFloat(item.price) || 0;
            subtotal += itemPrice;
            return this.renderSummaryItem(item.image, item.name, itemPrice, item.details, isReviewStep);
        }).join("");

        return summaryHTML
            ? `<div class="row g-3">${summaryHTML}</div><hr> 
               <h4 class="fw-bold text-end text-primary">Subtotal: ${formatPrice(subtotal)}</h4>`
            : '<p class="text-center">No selections yet.</p>';
    }

    /**
     * Formats pool summary details.
     * @param {Object} selectedPool - Selected pool object.
     * @returns {Object} - Formatted pool summary.
     */
    formatPoolSummary(selectedPool) {
        return {
            ...selectedPool,
            name: `${selectedPool.name} (${selectedPool.size.name})`,
            price: parseFloat(selectedPool.price) || 0,
            details: `
                <p class="mb-0"><strong>Wall Height:</strong> ${selectedPool.height}</p>
                <p class="mb-0"><strong>Material:</strong> ${selectedPool.material}</p>
            `
        };
    }

    /**
     * Formats liner summary details.
     * @param {Object} selectedLiner - Selected liner object.
     * @returns {Object} - Formatted liner summary.
     */
    formatLinerSummary(selectedLiner) {
        return {
            ...selectedLiner,
            name: `${selectedLiner.name} (${selectedLiner.size})`,
            price: parseFloat(selectedLiner.price) || 0,
            details: `<p class="mb-0"><strong>Type:</strong> ${selectedLiner.type}</p>`
        };
    }

    /**
     * Renders a summary item card.
     * @param {string} image - Image URL of the item.
     * @param {string} title - Item title.
     * @param {number} price - Item price.
     * @param {string} details - Additional item details.
     * @param {boolean} isReviewStep - Whether this is for the review step.
     * @returns {string} - HTML representation of the summary item.
     */
    renderSummaryItem(image, title, price, details = "", isReviewStep = false) {
        return `
            <div class="${isReviewStep ? 'col-md-4' : 'col-12'}">
                <div class="card mb-4 shadow-sm p-3 text-center rounded">
                    <img src="${image}" alt="${title}" class="img-fluid mx-auto mb-2 rounded w-100 h-auto" 
                        style="max-width: 120px; object-fit: contain;">
                    <h5 class="card-title fw-bold mb-2 fs-5">${title}</h5>
                    <p class="fw-semibold mb-2 fs-6"><strong>Price:</strong> ${formatPrice(price)}</p>
                    <div class="text-muted fs-6">${details}</div>
                </div>
            </div>
        `;
    }

    /**
     * Clears the entire summary.
     */
    clearSummary() {
        if (this.summaryContainer) this.summaryContainer.innerHTML = '<p class="text-center">No selections yet.</p>';
    }

    /**
     * Clears only the liner summary while keeping pool and skimmer selections.
     * @param {Object} selectedPool - Selected pool object.
     * @param {Object} selectedSkimmer - Selected skimmer object.
     */
    clearLinerSummary(selectedPool, selectedSkimmer) {
        this.updateSummary(selectedPool, null, selectedSkimmer);
    }

    /**
     * Clears the equipment summary while keeping previous selections.
     * @param {Object} selectedPool - Selected pool object.
     * @param {Object} selectedSkimmer - Selected skimmer object.
     * @param {Object} selectedLiner - Selected liner object.
     */
    clearEquipmentSummary(selectedPool, selectedSkimmer, selectedLiner) {
        this.updateSummary(selectedPool, selectedLiner, selectedSkimmer);
    }
}

export { Pool_SummaryManager };
